/**
 * Central polling functions for data that is shared on multiple pages/components.
 * Data is saved to the global state store.
 */

import { authClient, dashboardBffClient } from '@/api-client';
import { useAppInfoStore } from '@/stores/appInfo';
import { useUserStore } from '@/stores/user';

import { checkAll } from './alert-checks';
import { showFlash } from './alerts';
import constants from './constants';
import { info } from './logger';
import router from './router';
import { defaultErrorHandler } from './services';
import { useAccessRightsStore } from './stores/accessRights/accessRights';
import { useDrRestoreStore } from './stores/disasterRecovery/drRestoreStore';
import { useMainStore } from './stores/main';
import { routeTo } from './utils/cluster-utils';

/**
 * Fetch from an endpoint on the auth svc that will return 201 if the user is still authenticated,
 * and will return 401 if the user is not authenticated.
 * If 401, we send the user to the Logout page.
 * @return {Promise}
 */
function checkAuthStatus() {
  const appInfoStore = useAppInfoStore();
  const userStore = useUserStore();
  const { authType } = appInfoStore.systemInfo ?? {};

  if ([constants.AUTH_TYPE_OIDC, constants.AUTH_TYPE_TOKEN].includes(authType)) {
    return authClient.authstatus?.getAuthStatus().catch((err) => {
      if (err.response?.status === 401) {
        info('Auth check received 401. Sending user to Logout page.');
        router.push({ name: 'Logout' });
        userStore.logOut();
      }
    });
  }

  return Promise.resolve();
}

export function healthCheckHasErrors(hc = {}) {
  // if any k10 services or the multi-cluster connection have not passed
  return hc.services?.some((check) => !check.passed) || (hc.mcconn && !hc.mcconn.passed);
}

async function fetchHealthStatus() {
  const store = useMainStore();
  const appInfoStore = useAppInfoStore();
  const drRestoreStore = useDrRestoreStore();
  const isAdmin = await useAccessRightsStore().waitForIsAdmin();

  try {
    const { data } = await dashboardBffClient.k10HealthCheck.k10HealthCheck();
    const k10DrRunning = data.dr?.restoreStatus === 'running';

    if (
      healthCheckHasErrors(data) &&
      // Don't show the system status message to non-admins
      isAdmin &&
      !k10DrRunning && // <- soon to be deprecated but need to support both dr workflows at the moment
      !drRestoreStore.drRestoreStatus.isRestoreRunning && // don't show system error alerts when a dr restore is running
      !(appInfoStore.isMCPrimary && !appInfoStore.isMCAdmin) &&
      // don't show alert when on the status page
      router.currentRoute.value.name !== 'Status'
    ) {
      showFlash({
        message: 'Kasten system is reporting errors',
        details: `This could be a configuration problem.`,
        messageType: 'danger',
        icon: 'icon-alert',
        dismiss: '12000',
        buttons: [
          {
            text: 'See details on System Status page',
            icon: 'icon-chevron-left',
            clickFn() {
              routeTo({ name: 'Status' });
            },
          },
        ],
      });
    }
    store.healthStatus = data;
  } catch (error) {
    defaultErrorHandler(error);
  }
}

function checkDrRestoreStatus() {
  const drRestoreStore = useDrRestoreStore();

  drRestoreStore.drRestoreStatus.drRestoreStatusRequest().catch(defaultErrorHandler);
  if (drRestoreStore.drRestoreStatus.drRestoreStatusResponse?.data?.status === 'running') {
    drRestoreStore.drRestoreStatus.pollRestoreStatus();
  }
}

export function pollingLoop() {
  const store = useMainStore();

  if (store.xhrEnabled === false) return;

  return Promise.all([checkAuthStatus(), fetchHealthStatus(), checkDrRestoreStatus(), checkAll()]);
}
